import { BankTransaction } from '@/store/transactionsApi';
import {
  BarChartPro,
  BarItemIdentifier,
  useChartProApiRef,
} from '@mui/x-charts-pro';
import { useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import type { VisibilityIdentifier } from '@mui/x-charts/plugins';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import {
  getCurrencyStringTitleForChart,
  formatNumberToCurrency,
} from '@/utils/currency';
import { useLocalization } from '@/hooks/useLocalization';
import { useGraphColors } from '@/hooks/useGraphColors';
import { parseAppDate } from '@/utils/date';
import { formatChartDateAxis } from '@/utils/charts/axisFormatters';

const BAR_CHART_HEIGHT = 400;

export type CashFlowTabMode = 'both' | 'income' | 'spending';

interface CashFlowDetailsBarChartProps {
  transactions: BankTransaction[];
  tabMode: CashFlowTabMode;
  startDate: string;
  endDate: string;
  onMonthSelect?: (month: string, category?: string) => void;
}

export interface ChartRef {
  exportAsPrint: () => void;
}

interface MonthlyData {
  // Raw ISO start-of-month; formatted only at display time.
  date: string;
  [key: string]: string | number; // For dynamic category properties
}

// Internal key used for the aggregate "Other" bar (top-N + Other strategy).
// Must NOT match any real category name, since real categories are passed
// through to CashFlowTable's filter (which matches by CategoryName /
// ParentCategoryName).
const OTHER_KEY = '__other__';
const NO_HIDDEN_ITEMS: VisibilityIdentifier<'bar'>[] = [];

const getMonthLabelsInRange = (startDate: string, endDate: string) => {
  const start = parseAppDate(startDate)?.startOf('month');
  const end = parseAppDate(endDate)?.startOf('month');

  if (!start || !end) {
    return [];
  }

  const [rangeStart, rangeEnd] = start.isAfter(end)
    ? [end, start]
    : [start, end];
  const months: MonthlyData[] = [];
  let current = rangeStart;

  while (current.isBefore(rangeEnd) || current.isSame(rangeEnd, 'month')) {
    months.push({ date: current.format() });
    current = current.add(1, 'month');
  }

  return months;
};

const createEmptyMonthlyTotals = (monthLabels: MonthlyData[]) =>
  monthLabels.reduce<Record<string, { income: number; spending: number }>>(
    (totals, month) => {
      totals[month.date] = { income: 0, spending: 0 };
      return totals;
    },
    {},
  );

const createEmptyMonthlyCategories = (monthLabels: MonthlyData[]) =>
  monthLabels.reduce<Record<string, Record<string, number>>>(
    (categories, month) => {
      categories[month.date] = {};
      return categories;
    },
    {},
  );

const CashFlowDetailsBarChart = forwardRef<
  ChartRef,
  CashFlowDetailsBarChartProps
>(({ transactions, tabMode, startDate, endDate, onMonthSelect }, ref) => {
  const { t } = useLocalization();
  const apiRef = useChartProApiRef<'bar'>();
  const graphColors = useGraphColors();

  const chartData = useMemo(() => {
    const monthLabels = getMonthLabelsInRange(startDate, endDate);

    if (tabMode === 'both') {
      // Both mode: show income and spending side by side
      const monthlyTotals: {
        [month: string]: { income: number; spending: number };
      } = createEmptyMonthlyTotals(monthLabels);

      transactions.forEach((transaction) => {
        const transactionDate = parseAppDate(transaction.TransactionDate);

        if (!transactionDate) return;
        const month = transactionDate.startOf('month').format();
        if (!Object.prototype.hasOwnProperty.call(monthlyTotals, month)) return;
        const amount = Math.abs(transaction.Amount);

        if (transaction.CashflowGroup.toLowerCase() === 'income') {
          monthlyTotals[month].income += amount;
        } else if (transaction.CashflowGroup.toLowerCase() === 'spending') {
          monthlyTotals[month].spending += amount;
        }
      });

      const data = monthLabels.map((x) => ({
        date: x.date,
        income: monthlyTotals[x.date].income,
        spending: monthlyTotals[x.date].spending,
      }));

      const series = [
        {
          dataKey: 'income',
          label: t('income'),
          color: graphColors[0] ?? 'rgba(111, 221, 152, 1)',
          stack: 'group1',
          valueFormatter: (value: number | null) =>
            formatNumberToCurrency(value ?? 0),
        },
        {
          dataKey: 'spending',
          label: t('spending'),
          color: graphColors[1] ?? 'rgba(242, 149, 89, 1)',
          stack: 'group2',
          valueFormatter: (value: number | null) =>
            formatNumberToCurrency(value ?? 0),
        },
      ];

      return { data, series };
    } else {
      // Income Details or Spending Details: show stacked categories
      const targetGroup = tabMode === 'income' ? 'income' : 'spending';
      const monthlyCategories: {
        [month: string]: { [category: string]: number };
      } = createEmptyMonthlyCategories(monthLabels);

      transactions
        .filter((t) => t.CashflowGroup.toLowerCase() === targetGroup)
        .forEach((transaction) => {
          const transactionDate = parseAppDate(transaction.TransactionDate);
          if (!transactionDate) return;
          const month = transactionDate.startOf('month').format();
          if (!Object.prototype.hasOwnProperty.call(monthlyCategories, month)) {
            return;
          }
          const category =
            transaction.ParentCategoryName ||
            transaction.CategoryName ||
            'Uncategorized';
          const amount = Math.abs(transaction.Amount);

          monthlyCategories[month][category] =
            (monthlyCategories[month][category] || 0) + amount;
        });

      // Sort categories by total amount (descending) and take top categories
      const categoryTotals = new Map<string, number>();
      Object.values(monthlyCategories).forEach((monthData) => {
        Object.entries(monthData).forEach(([category, amount]) => {
          categoryTotals.set(
            category,
            (categoryTotals.get(category) || 0) + amount,
          );
        });
      });

      const TOP_CATEGORY_LIMIT = 5;
      const sortedCategories = Array.from(categoryTotals.entries()).sort(
        ([, a], [, b]) => b - a,
      );
      const topCategories = sortedCategories
        .slice(0, TOP_CATEGORY_LIMIT)
        .map(([category]) => category);
      const otherCategories = sortedCategories
        .slice(TOP_CATEGORY_LIMIT)
        .map(([category]) => category);
      const hasOther = otherCategories.length > 0;

      // Generate data for each month — accumulate "Other" as sum of leftover categories.
      const data: MonthlyData[] = monthLabels.map((date) => {
        const monthData: MonthlyData = { date: date.date };
        topCategories.forEach((category) => {
          monthData[category] = monthlyCategories[date.date][category] || 0;
        });
        if (hasOther) {
          const otherSum = otherCategories.reduce(
            (sum, cat) => sum + (monthlyCategories[date.date][cat] || 0),
            0,
          );
          monthData[OTHER_KEY] = otherSum;
        }
        return monthData;
      });

      const categoryColors = graphColors;
      const displayedCategories = hasOther
        ? [...topCategories, OTHER_KEY]
        : topCategories;

      const series = displayedCategories.map((category, index) => {
        const isOther = category === OTHER_KEY;
        return {
          id: category,
          dataKey: category,
          label: isOther
            ? `${t('other') || 'Other'} (${String(otherCategories.length)})`
            : category === 'Uncategorized'
              ? t('uncategorized')
              : category,
          color: categoryColors[index % categoryColors.length],
          stack: 'categories',
          valueFormatter: (value: number | null) =>
            formatNumberToCurrency(value ?? 0),
        };
      });

      return { data, series };
    }
  }, [endDate, graphColors, startDate, transactions, tabMode, t]);

  const [hiddenState, setHiddenState] = useState({
    tabMode,
    items: NO_HIDDEN_ITEMS,
  });
  if (hiddenState.tabMode !== tabMode) {
    setHiddenState({ tabMode, items: NO_HIDDEN_ITEMS });
  }
  const hiddenItems =
    hiddenState.tabMode === tabMode ? hiddenState.items : NO_HIDDEN_ITEMS;
  const handleHiddenItemsChange = (items: VisibilityIdentifier<'bar'>[]) => {
    setHiddenState({ tabMode, items });
  };

  const { visibleData, series } = useMemo(() => {
    const hiddenIds = new Set(hiddenItems.map((item) => item.seriesId));
    if (hiddenIds.size === 0) {
      return { visibleData: chartData.data, series: chartData.series };
    }
    return {
      visibleData: chartData.data.map((monthData) => {
        const visibleMonth: MonthlyData = { ...monthData };
        hiddenIds.forEach((id) => {
          if (typeof id === 'string' && id in visibleMonth) {
            visibleMonth[id] = 0;
          }
        });
        return visibleMonth;
      }),
      series: chartData.series.map((item) =>
        'id' in item && hiddenIds.has(item.id)
          ? { ...item, valueFormatter: () => null }
          : item,
      ),
    };
  }, [chartData, hiddenItems]);

  const handleItemClick = (
    _event: React.MouseEvent<SVGElement>,
    datum: BarItemIdentifier,
  ) => {
    if (!onMonthSelect || !chartData.data[datum.dataIndex]) return;

    const month = chartData.data[datum.dataIndex].date;
    const seriesId = String(datum.seriesId);
    // When user clicks the aggregate "Other" bar, do not filter by category —
    // OTHER_KEY is an internal key that does not match any real CategoryName,
    // so passing it through would yield "No rows" in CashFlowTable. Instead,
    // open the month view without a category filter so the user can see all
    // transactions in the leftover categories.
    const category =
      tabMode === 'both' || seriesId === OTHER_KEY ? undefined : seriesId;
    onMonthSelect(month, category);
  };

  const onBeforeExport = (iframe: HTMLIFrameElement) => {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      const svg = doc.querySelector('svg');
      if (!svg) return;
      const computedColor =
        (doc.defaultView || window).getComputedStyle(svg).color || '#000';
      const textNodes = doc.querySelectorAll('text');
      textNodes.forEach((t: Element) => {
        (t as SVGTextElement).setAttribute('fill', computedColor);
        (t as SVGTextElement).style.fill = computedColor;
        (t as SVGTextElement).style.stroke = 'none';
      });
      const axisStrokes = doc.querySelectorAll('line, path');
      axisStrokes.forEach((el: Element) => {
        (el as SVGElement).style.stroke = computedColor;
      });
      void iframe.offsetHeight;
    } catch {
      console.error('Failed to export as print');
    }
  };

  useImperativeHandle(ref, () => ({
    exportAsPrint: async () => {
      const chartApi = apiRef.current;

      if (chartApi && 'exportAsPrint' in chartApi) {
        try {
          const el = document.querySelector('[data-mui-chart]');
          el?.getBoundingClientRect();
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                resolve();
              }),
            ),
          );
          setTimeout(() => {
            chartApi.exportAsPrint({
              copyStyles: true,
              onBeforeExport,
            });
          }, 50);
        } catch {
          setTimeout(() => {
            chartApi.exportAsPrint({
              copyStyles: true,
              onBeforeExport,
            });
          }, 50);
        }
      }
    },
  }));

  return (
    <BarChartPro
      apiRef={apiRef}
      height={BAR_CHART_HEIGHT}
      // showToolbar
      yAxis={[
        {
          valueFormatter: (value: number) => {
            return getCurrencyStringTitleForChart(value);
          },
        },
      ]}
      series={series}
      dataset={visibleData}
      hiddenItems={hiddenItems}
      onHiddenItemsChange={handleHiddenItemsChange}
      xAxis={[
        {
          height: 50,
          scaleType: 'band',
          dataKey: 'date',
          valueFormatter: (value: string | Date | null, ctx) =>
            formatChartDateAxis(value, ctx),
          tickLabelInterval: 'auto',
        },
      ]}
      sx={{
        width: '100%',
        [`& .${axisClasses.directionY} .${axisClasses.label}`]: {
          transform: 'translateX(-10px)',
        },
        '& text': {
          fill: 'currentColor !important',
        },
        [`& .${axisClasses.root} line, & .${axisClasses.root} path`]: {
          stroke: 'currentColor !important',
        },
        '@media print': {
          color: '#000',
        },
        // Control bar spacing and max width through CSS
        '& .MuiBarElement-root': {
          maxWidth: '120px',
        },
      }}
      slotProps={{
        legend: {
          position: { vertical: 'bottom' },
          toggleVisibilityOnClick: tabMode !== 'both',
        },
        toolbar: {
          printOptions: {
            copyStyles: true,
            onBeforeExport,
          },
        },
      }}
      borderRadius={8}
      onItemClick={handleItemClick}
    />
  );
});

CashFlowDetailsBarChart.displayName = 'CashFlowDetailsBarChart';

export default CashFlowDetailsBarChart;
